import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CircularProgress,
  Button,
  Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { fetchAccessoriesDev, fetchProductsByCategory } from "../api/productApi";

export default function AccessoriesPage() {
  const navigate = useNavigate();
  const [data, setData] = useState({ women: { product_types: [], total_count: 0 }, men: { product_types: [], total_count: 0 } });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadAccessories = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await fetchAccessoriesDev();
        setData({
          women: result.women || { product_types: [], total_count: 0 },
          men: result.men || { product_types: [], total_count: 0 },
        });
      } catch (err) {
        console.error("Failed to load accessories", err);
        setError(err.message || "Failed to load accessories");
        setData({ women: { product_types: [], total_count: 0 }, men: { product_types: [], total_count: 0 } });
      } finally {
        setLoading(false);
      }
    };

    loadAccessories();
  }, []);

  const handleAccessoryTypeClick = async (productTypeName, gender) => {
    try {
      const products = await fetchProductsByCategory("Accessories", productTypeName, false, gender || "women");
      navigate("/products", {
        state: {
          products,
          category: "Accessories",
          subcategory: productTypeName,
        },
      });
    } catch (err) {
      console.error("Failed to load accessories products", err);
      alert("Could not load products. Please try again.");
    }
  };

  const renderTypeCard = (type, gender) => (
    <Grid item xs={12} sm={6} md={4} lg={3} key={type.product_type_name}>
      <Card
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          cursor: "pointer",
          transition: "all 0.3s ease",
          borderRadius: 2,
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 8px 16px rgba(0,0,0,0.12)",
          },
        }}
        onClick={() => handleAccessoryTypeClick(type.product_type_name, gender)}
      >
        <CardMedia
          component="img"
          height="300"
          image={
            type.image_url
              ?.replace("storage.cloud.google.com", "storage.googleapis.com") ||
            `https://picsum.photos/seed/${type.product_type_name}/400/300`
          }
          alt={type.product_type_name}
          sx={{
            objectFit: "cover",
            backgroundColor: "grey.100",
          }}
          onError={(e) => {
            e.target.src = `https://picsum.photos/seed/${type.product_type_name}/400/300`;
          }}
        />
        <CardContent
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            p: 2,
          }}
        >
          <Typography
            variant="h6"
            component="h2"
            sx={{
              fontWeight: 600,
              mb: 1,
              textTransform: "capitalize",
              color: "text.primary",
            }}
          >
            {type.product_type_name}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "0.875rem" }}
          >
            {type.product_count} {type.product_count === 1 ? "item" : "items"}
          </Typography>
        </CardContent>
      </Card>
    </Grid>
  );

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 8, textAlign: "center" }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2, color: "text.secondary" }}>
          Loading accessories...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 8, textAlign: "center" }}>
        <Typography variant="h5" color="error" sx={{ mb: 2 }}>
          Error loading accessories
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          {error}
        </Typography>
        <Button
          variant="outlined"
          onClick={() => navigate("/")}
          startIcon={<ArrowBackIcon />}
        >
          Go Back Home
        </Button>
      </Container>
    );
  }

  const womenTypes = data.women?.product_types || [];
  const menTypes = data.men?.product_types || [];
  const hasWomen = womenTypes.length > 0;
  const hasMen = menTypes.length > 0;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{
            textTransform: "none",
            color: "text.secondary",
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          Back
        </Button>
        <Typography
          variant="h5"
          component="h1"
          sx={{
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 1,
            color: "text.primary",
          }}
        >
          Accessories
        </Typography>
      </Box>

      {!hasWomen && !hasMen ? (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            No accessories available
          </Typography>
        </Box>
      ) : (
        <Box>
          {/* Women Accessories */}
          {hasWomen && (
            <Box sx={{ mb: 6 }}>
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="h4"
                  component="h2"
                  sx={{
                    fontWeight: 600,
                    textTransform: "capitalize",
                    color: "text.primary",
                    mb: 1,
                  }}
                >
                  Women
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: "0.875rem" }}
                >
                  {data.women.total_count} {data.women.total_count === 1 ? "item" : "items"} total
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={4}>
                {womenTypes.map((type) => renderTypeCard(type, "women"))}
              </Grid>
            </Box>
          )}

          {/* Men Accessories */}
          {hasMen && (
            <Box sx={{ mb: 6 }}>
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="h4"
                  component="h2"
                  sx={{
                    fontWeight: 600,
                    textTransform: "capitalize",
                    color: "text.primary",
                    mb: 1,
                  }}
                >
                  Men
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: "0.875rem" }}
                >
                  {data.men.total_count} {data.men.total_count === 1 ? "item" : "items"} total
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={4}>
                {menTypes.map((type) => renderTypeCard(type, "men"))}
              </Grid>
            </Box>
          )}
        </Box>
      )}

      {(hasWomen || hasMen) && (
        <Box sx={{ mt: 6, textAlign: "center" }}>
          <Typography variant="body1" color="text.secondary">
            Showing {womenTypes.length + menTypes.length} accessory{" "}
            {womenTypes.length + menTypes.length === 1 ? "type" : "types"}
          </Typography>
        </Box>
      )}
    </Container>
  );
}
